/**
 * # SearchAndSelect
 *
 * A component that provides a search input and a dropdown list for selecting items.
 *
 * !!! note
 *     It is generally not recommended to use the `SearchAndSelect` component by itself. Instead use `MultiSearchAndSelect` or `SingleSearchAndSelect`, which both inherit the props defined here.
 *
 * ## Props
 *
 * - `className` (string): Additional CSS class name for the component.
 * - `data` (ImmutablePropTypes.list): The list of data items to be displayed in the dropdown.
 * - `selectedItems` (ImmutablePropTypes.list): The list of selected items.
 * - `inputType` (string): The type of input to be rendered. Can be 'search' or 'text'.
 * - `label` (string): The label for the input field.
 * - `defaultValue` (string): The default value for the search input.
 * - `query` (queryBuilderPropType): The query builder for fetching data.
 * - `openOnFocus` (boolean): Whether to open the dropdown on input focus.
 * - `iconRight` (IconProp): The icon to be displayed on the right side of the input field.
 * - `replaceOnSelect` (boolean): Whether to replace the search input value with the selected item's content.
 * - `closeOnSelect` (boolean): Whether to close the dropdown after selecting an item.
 * - `searchFilter` (function): The function used to filter the search results.
 * - `content` (function): The function used to render the content of each dropdown item.
 * - `listItemContent` (function): The function used to render the content of each dropdown item in the list.
 * - `onSelect` (function): The function called when an item is selected.
 * - `onClear` (function): The function called when the search input is cleared.
 * - `onChange` (function): The function called when the search input value changes.
 * - `customFooterRenderer` (function): The function used to render a custom footer in the dropdown.
 * - `error` (boolean): Whether there is an error in the input field.
 * - `placeholder` (string): The placeholder text for the input field.
 * - 'message' (string): Optional message to display below the input field.
 * - `useMenuStyle` (boolean): Whether to use Menu component style instead of Card/DropdownItem. Default is false.
 * - `menuContentClassName` (string): Additional CSS class name for Menu.Content when useMenuStyle is true.
 *
 * ## Usage
 *
 * ```jsx
 * import SearchAndSelect from './SearchAndSelect.react';
 *
 * const data = [
 *   { id: 1, name: 'Item 1' },
 *   { id: 2, name: 'Item 2' },
 *   { id: 3, name: 'Item 3' },
 * ];
 *
 * const MyComponent = () => {
 *   const handleSelect = (item) => {
 *     console.log('Selected item:', item);
 *   };
 *
 *   return (
 *     <SearchAndSelect
 *       data={data}
 *       selectedItems={[]}
 *       inputType="search"
 *       label="Search"
 *       defaultValue=""
 *       query={null}
 *       openOnFocus={false}
 *       iconRight={null}
 *       replaceOnSelect={false}
 *       closeOnSelect={false}
 *       searchFilter={(query, searchString) => {
 *         // Implement your search filter logic here
 *         return data.filter((item) => item.name.includes(searchString));
 *       }}
 *       content={(item) => item.name}
 *       listItemContent={null}
 *       onSelect={handleSelect}
 *       onClear={() => {
 *         console.log('Search cleared');
 *       }}
 *       onChange={(value) => {
 *         console.log('Search value changed:', value);
 *       }}
 *       customFooterRenderer={null}
 *       error={false}
 *       placeholder="Search items"
 *     />
 *   );
 * };
 * ```
 */
import React from 'react';
import PropTypes from 'prop-types';
import ImmutablePropTypes from 'react-immutable-proptypes';
import {List} from 'immutable';
import type {Iterable as ImmutableIterable} from 'immutable';
import classnames from 'classnames';
import {Manager, Target, Popper} from 'react-popper';
import {queryBuilderPropType} from '@albert-io/json-api-framework/request/builder';
import type {QueryBuilder} from '@albert-io/json-api-framework/request/builder';
import makeConstants from 'lib/makeConstants';
import Text from '@albert-io/atomic/atoms/Text/Text.react';
import Card from '@albert-io/atomic/atoms/Card/Card.react';
import DropdownItem from '@albert-io/atomic/molecules/DropdownItem/DropdownItem.react';
import SearchInput from '@albert-io/atomic/molecules/SearchInput/SearchInput.react';
import IconInput from '@albert-io/atomic/molecules/IconInput/IconInput.react';
import Field from '@albert-io/atomic/molecules/Field/Field.react';
import Menu from '@albert-io/atomic/organisms/Menu/Menu.react';
// IconProp is the icon-name type (string | [prefix, name] | lookup object) accepted by the Icon atom.
// It is a type-only export, so we import it as a type and describe it to PropTypes with a matching shape.
import type {IconProp} from '@albert-io/atomic/atoms/Icon/Icon.react';
import './search-and-select.scss';

const inputOptions = makeConstants('search', 'text');

/**
 * The list of items rendered in the dropdown is an Immutable List of opaque
 * records whose shape is determined by the consumer; we keep the element type
 * `unknown` rather than papering over with `any`.
 */
type SearchAndSelectDatum = unknown;

interface SearchAndSelectProps {
  className?: string;
  data: List<SearchAndSelectDatum>;
  selectedItems: List<SearchAndSelectDatum>;
  inputType: string;
  label: string;
  defaultValue: string;
  query?: QueryBuilder | null;
  openOnFocus: boolean;
  iconRight?: IconProp | null;
  replaceOnSelect: boolean;
  clearOnSelect: boolean;
  closeOnSelect: boolean;
  // `searchFilter` is polymorphic in legacy usage: when given a `query` builder it
  // returns a filtered `QueryBuilder` (used in `fetchData`); when given just a search
  // string it returns an Immutable-compatible predicate used to filter local `data`.
  searchFilter: (
    query: QueryBuilder | string | null | undefined,
    searchString?: string
  ) => QueryBuilder | ((value: SearchAndSelectDatum, key: number) => boolean) | null | undefined;
  content: (datum: SearchAndSelectDatum) => React.ReactNode;
  listItemContent?: (datum: SearchAndSelectDatum) => React.ReactNode;
  onSelect: (selectedItem: SearchAndSelectDatum) => void;
  onClear: () => void;
  onChange: (searchString: string) => void;
  customFooterRenderer?: React.ComponentType<{
    searchTerm: string;
    resetSearch: () => void;
  }> | null;
  error: boolean;
  placeholder?: string;
  message: string;
  useMenuStyle: boolean;
  menuContentClassName: string;
}

interface SearchAndSelectState {
  show: boolean;
  searchString: string;
  data: List<SearchAndSelectDatum>;
  isQuerySearch: boolean;
}

export default class SearchAndSelect extends React.Component<
  SearchAndSelectProps,
  SearchAndSelectState
> {
  toggleRef: React.RefObject<HTMLDivElement>;

  // Ref to the underlying SearchInput class instance; we reach into its `state.value`
  // imperatively (legacy pattern), so we type only the slice we touch.
  inputRef: React.RefObject<{state: {value: string}}>;

  static propTypes = {
    className: PropTypes.string,
    data: ImmutablePropTypes.list,
    selectedItems: ImmutablePropTypes.list,
    inputType: PropTypes.oneOf(Object.values(inputOptions)),
    label: PropTypes.string,
    defaultValue: PropTypes.string,
    query: queryBuilderPropType,
    openOnFocus: PropTypes.bool,
    iconRight: PropTypes.oneOfType([PropTypes.string, PropTypes.array, PropTypes.object]),
    replaceOnSelect: PropTypes.bool,
    clearOnSelect: PropTypes.bool,
    closeOnSelect: PropTypes.bool,
    searchFilter: PropTypes.func.isRequired,
    content: PropTypes.func.isRequired,
    listItemContent: PropTypes.func,
    onSelect: PropTypes.func,
    onClear: PropTypes.func,
    onChange: PropTypes.func,
    customFooterRenderer: PropTypes.func,
    error: PropTypes.bool,
    placeholder: PropTypes.string,
    message: PropTypes.string,
    useMenuStyle: PropTypes.bool,
    menuContentClassName: PropTypes.string
  };

  static defaultProps = {
    data: List(),
    selectedItems: List(),
    inputType: 'search',
    label: '',
    replaceOnSelect: false,
    clearOnSelect: false,
    openOnFocus: false,
    iconRight: null,
    closeOnSelect: false,
    defaultValue: '',
    onSelect: () => {},
    onClear: () => {},
    onChange: () => {},
    error: false,
    message: '',
    useMenuStyle: false,
    menuContentClassName: ''
  };

  constructor(props: SearchAndSelectProps) {
    super(props);

    this.state = {
      show: false,
      searchString: props.defaultValue,
      data: props.data,
      isQuerySearch: !!props.query
    };

    this.toggleRef = React.createRef();
    this.inputRef = React.createRef();
  }

  componentDidMount() {
    if (this.props.query) {
      this.fetchData();
    }

    global.document.addEventListener('click', this.handleClickOutsideComponent);
  }

  componentDidUpdate(prevProps: SearchAndSelectProps, prevState: SearchAndSelectState) {
    if (prevState.searchString !== this.state.searchString && this.props.query) {
      this.fetchData();
    }

    if (prevProps.data !== this.props.data) {
      this.setInitialData();
    }
  }

  componentWillUnmount() {
    global.document.removeEventListener('click', this.handleClickOutsideComponent);
  }

  async fetchData() {
    const {searchString} = this.state;
    const filteredQuery = this.props.searchFilter(this.props.query, searchString) as
      | QueryBuilder
      | null
      | undefined;
    if (filteredQuery) {
      // getResourcePromise resolves an Immutable List of opaque JSON:API resources at this dynamic boundary.
      const data = (await filteredQuery.getResourcePromise()) as List<SearchAndSelectDatum>;
      this.setState({data});
    } else {
      this.setState({data: List()});
    }
  }

  setInitialData = () => {
    this.setState({data: this.props.data});
  };

  handleClickOutsideComponent = (e: MouseEvent) => {
    if (!this.toggleRef.current!.contains(e.target as Node)) {
      this.toggleDropdown(false);
    }
  };

  // Esc key closes results
  handleEscapeKeyPress = (key: string) => {
    if (this.state.show && key === 'Escape') {
      this.toggleDropdown(false);
    }
  };

  handleKeyDownOnSelect = (e: React.KeyboardEvent, datum: SearchAndSelectDatum) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      this.setSelection(datum);
    }
  };

  toggleDropdown = (isOpen?: boolean) => {
    this.setState((state) => {
      const show = isOpen !== undefined ? isOpen : !state.show;

      return {show};
    });
  };

  setSearchString = (searchString: string) => {
    this.setState(
      {
        searchString,
        show: this.props.openOnFocus ? true : !!searchString
      },
      () => this.props.onChange(searchString)
    );
  };

  setSelection = (selectedItem: SearchAndSelectDatum) => {
    if (this.props.replaceOnSelect) {
      if (this.props.inputType === 'search') {
        this.inputRef.current!.state.value = this.props.content(selectedItem) as string;
      }

      this.setState({
        searchString: this.props.content(selectedItem) as string
      });
    } else if (this.props.clearOnSelect) {
      this.resetSearch();
    }

    this.props.onSelect(selectedItem);
    if (this.props.closeOnSelect) {
      this.toggleDropdown(false);
    }
  };

  resetSearch = () => {
    const hadSearchString = !!this.state.searchString;
    this.setState(
      {
        show: this.props.openOnFocus ? true : false,
        searchString: ''
      },
      () => {
        if (hadSearchString) {
          this.props.onClear();
        }
      }
    );
  };

  onFocus = () => {
    if (this.state.searchString || this.props.openOnFocus) {
      this.toggleDropdown(true);
      if (this.props.query) {
        this.fetchData();
      }
    }
  };

  handleIconClick = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    e?.preventDefault();
    this.toggleDropdown();
  };

  makeInput = () => {
    const {inputType} = this.props;

    if (inputType === 'search') {
      return (
        <SearchInput
          className='o-search-select__search-input'
          onReset={this.resetSearch}
          onChange={(e) => this.setSearchString(e.target.value)}
          onFocus={this.onFocus}
          placeholder='Search'
          error={this.props.error}
          // Legacy imperative ref: setSelection reaches into the input instance's `state.value`.
          // SearchInput types its ref as Ref<HTMLInputElement>, so we bridge the two shapes here.
          ref={this.inputRef as unknown as React.Ref<HTMLInputElement>}
        />
      );
    }

    return (
      <Field
        as={IconInput}
        label={this.props.label}
        border
        fillWrapper
        onChange={(e) => this.setSearchString(e.target.value)}
        onFocus={this.onFocus}
        onKeyDown={(e) => this.handleEscapeKeyPress(e.key)}
        iconRight={this.state.searchString ? 'times' : this.props.iconRight}
        placeholder={this.props.placeholder || 'Type to search'}
        value={this.state.searchString}
        defaultValue={this.state.searchString}
        onIconRightClick={this.state.searchString ? this.resetSearch : this.handleIconClick}
        alwaysUseIcon={this.props.useMenuStyle}
        error={this.props.error}
      />
    );
  };

  makeSearchResults = () => {
    // `searchResults` starts as the full List but may be narrowed to a filtered Iterable
    // (Immutable 3.8's `filter` returns the base Iterable type, not List).
    let searchResults: ImmutableIterable<number, SearchAndSelectDatum> = this.state.data;

    if (!this.state.isQuerySearch) {
      const filterWithString = this.props.searchFilter(this.state.searchString) as (
        value?: SearchAndSelectDatum,
        key?: number
      ) => boolean;
      searchResults = this.state.data.filter(filterWithString);
    }

    if (searchResults.isEmpty()) {
      return (
        <Text as='div' className='o-search-select__empty-results p-x2!' color='tertiary'>
          No results found
        </Text>
      );
    }

    if (this.props.useMenuStyle) {
      return searchResults
        .map((datum, key) => {
          const isSelected = this.props.selectedItems.includes(datum);

          return (
            <Menu.Item key={key} selected={isSelected} onClick={() => this.setSelection(datum)}>
              {this.props.listItemContent
                ? this.props.listItemContent(datum)
                : this.props.content(datum)}
            </Menu.Item>
          );
        })
        .toArray();
    }

    return searchResults
      .map((datum, key) => {
        const isSelected = this.props.selectedItems.includes(datum);

        return (
          <DropdownItem
            aria-checked={isSelected}
            role='checkbox'
            tabIndex={0}
            className='o-search-select__item'
            onClick={() => this.setSelection(datum)}
            onKeyDown={(e) => this.handleKeyDownOnSelect(e, datum)}
            icon={isSelected ? 'check' : undefined}
            key={key}
          >
            {this.props.listItemContent
              ? this.props.listItemContent(datum)
              : this.props.content(datum)}
          </DropdownItem>
        );
      })
      .toArray();
  };

  render() {
    const {show} = this.state;
    const {message, error, useMenuStyle, menuContentClassName} = this.props;
    const Footer = this.props.customFooterRenderer;

    const resultsContent = (
      <>
        {this.makeSearchResults()}
        {Footer && <Footer searchTerm={this.state.searchString} resetSearch={this.resetSearch} />}
      </>
    );

    const contentWrapper = useMenuStyle ? (
      <div
        className={classnames(
          'o-search-select__menu-content rounded-x1 border border-border-primary background-primary',
          menuContentClassName
        )}
        onKeyDown={(e) => this.handleEscapeKeyPress(e.key)}
      >
        {resultsContent}
      </div>
    ) : (
      <Card
        // Card forwards arbitrary DOM props via ...rest at runtime but its Props type omits
        // HTML attributes like onKeyDown. Proper fix lives in Card.react.tsx (crossFile).
        // @ts-expect-error onKeyDown is not declared on Card's Props
        onKeyDown={(e) => this.handleEscapeKeyPress(e.key)}
        className='o-search-select__content'
      >
        {resultsContent}
      </Card>
    );

    return (
      <div className={classnames('o-search-select', this.props.className)} ref={this.toggleRef}>
        <Manager>
          <Target>{this.makeInput()}</Target>
          {!show && message ? <Message error={error}>{message}</Message> : null}
          {show && (
            <Popper
              className='o-search-select__content-wrapper elevation-shadow-l3'
              placement='bottom-start'
              modifiers={{
                flip: {
                  enabled: false
                },
                preventOverflow: {
                  boundariesElement: 'window'
                }
              }}
            >
              {contentWrapper}
            </Popper>
          )}
        </Manager>
      </div>
    );
  }
}

interface MessageProps {
  children?: React.ReactNode;
  error?: boolean;
}

const Message = ({children, error}: MessageProps) => (
  <Text
    className={classnames('o-search-select__message !content-primary', {
      'o-search-select__message--error': error
    })}
    size='xs'
  >
    {children}
  </Text>
);

Message.propTypes = {
  children: PropTypes.node,
  error: PropTypes.bool
};
